// CostBreakdownModal.tsx

import React, { useState, useCallback } from 'react';
import { OverlayPage } from '@components/partials/OverlayPage'; 
import { CostBreakdownTable, Banner } from '@cloudclevr/components';

import { useCostBreakdownQuery, useCostBreakdownFiltersQuery } from '@hooks/roadmaps/useCostbreakdown';
import './quill-styles.css';
 
interface CostBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  roadmapId: string;
  customerName: string;
  currentPath?: string;
  categoryId?: string;
  caption?: string;
}

const CostBreakdownModal: React.FC<CostBreakdownModalProps> = ({
  isOpen,
  onClose,
  roadmapId,
  customerName,
  currentPath,
  categoryId,
  caption
}) => {
  // State for managing search and filter parameters for injected hooks
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

// Inject hooks here for separation of concerns - these will be passed to the table
const costQuery = useCostBreakdownQuery({
  roadmapId,
  searchTerm,
  categories: selectedCategories,
  enabled: !!roadmapId && isOpen
}) as any; // Type assertion for React Query v5 compatibility with component library

// Ensure filters are fetched only when the modal is open so the table
// receives category options promptly when the overlay opens.
const filtersQuery = useCostBreakdownFiltersQuery(roadmapId, isOpen) as any;

  // Callback to handle search changes from the table
  const handleSearchChange = useCallback((newSearchTerm: string, newCategories: string[]) => {
    setSearchTerm(newSearchTerm);
    setSelectedCategories(newCategories);
  }, []);

  if (!isOpen) return null;
  const defaultPath = `/roadmaps/${roadmapId || ''}`;

  return (
    <OverlayPage
      data-testid="cost-breakdown-modal"
      isFooterVisible={false}
      contentClassNames="!px-0 !pb-0"
      hasScroll={false}
      handleOnClose={onClose}
      path={currentPath && currentPath.trim() !== '' ? currentPath : defaultPath}
      headerTitle={`${(caption || 'Technology roadmap')}: proposed projects`}

    >
      <div className="flex flex-col h-full" data-testid="cost-breakdown-modal-content">
        {/* Move title outside of scrolling area or make it part of header */}
        <div className="px-1 pt-4 md:px-6 xl:px-8 mb-3">
          <Banner
            variant="info"
            message={
              <span className="pt-[0.09rem]"><strong>Disclaimer:</strong>{' '}All costs shown are indicative only and are subject to a formal quotation. Actual costs may vary depending on specific requirements and scope.</span>
            }
            testId="cost-breakdown-disclaimer"
          />
        </div>
        
        {/* Table container with proper sticky context */}
        <div 
          className="flex-1 min-h-0 relative"
          data-testid="cost-breakdown-table-container"
        >
          <CostBreakdownTable
            roadmapId={roadmapId}
            categoryId={categoryId}
            customerName={customerName}
            injectedCostQuery={costQuery}
            injectedFiltersQuery={filtersQuery}
            onSearchChange={handleSearchChange}
            data-testid="cost-breakdown-table"
          />
        </div>
      </div>
    </OverlayPage>
  );
};

export default CostBreakdownModal;